/**
 * Phone Field Component
 * Form field for phone number input with formatting
 * @see mobile: module-customer/src/modules/addresses/components/forms/phone-field/phone-field.tsx
 */

'use client';

import { Controller, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PhoneInput } from '@kidslabs/mario-nextjs-react-ui-core';
import { useCallback, useMemo } from 'react';
import type { PhoneFieldProps } from './phone-field.types';
import { validatePhoneNumber } from './phone-field.schema';
import { formatPhoneNumber } from '../../../utils';

export const PhoneField = <T extends FieldValues>({
    name,
    control,
    errorMessage,
    disabled,
    placeholder,
    testID = 'phone-field',
    onResetValue,
}: PhoneFieldProps<T>) => {
    const { t } = useTranslation('auth');

    const flagImageProps = useMemo(
        () => ({
            // handle use url from cdn
            //src: getImageAssetUrl('images/flag.png'),
            src: '/assets/images/flag.png',
            alt: 'Quốc kỳ Việt Nam',
            objectFit: 'cover' as const,
            style: { objectPosition: 'center' },
        }),
        []
    );

    const handleResetValue = useCallback(() => {
        onResetValue?.();
    }, [onResetValue]);

    const validatePhone = useCallback(
        (value: string) => {
            // Using `replace` instead of `replaceAll` because the project TypeScript target/lib
            // does not include ES2021 yet. Updating the lib would impact multiple packages,
            // so we keep `replace` here for compatibility.
            const formattedValue = value.replace(/\s+/g, '');
            return validatePhoneNumber(formattedValue) || t('error_validate_phone_number_invalid');
        },
        [t]
    );

    return (
        <Controller
            control={control}
            name={name}
            rules={{ validate: validatePhone }}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <PhoneInput
                    label={t('text_phone_number')}
                    placeholder={placeholder ?? t('customer_input_phone')}
                    errorMessage={error?.message ?? errorMessage}
                    value={formatPhoneNumber(value ?? '')}
                    onChange={onChange}
                    onBlur={onBlur}
                    onReset={handleResetValue}
                    disabled={disabled}
                    data-testid={testID}
                    flagIconProps={flagImageProps}
                />
            )}
        />
    );
};
